import { postAuthorizedStreaming } from "@/lib/fetch";
import { ThreadMessage } from "@assistant-ui/react";
import { ReadableStreamDefaultReader } from "stream/web";
import { createStreamGenerator } from "./stream/streamGenerator";
import { getLastUserMessage } from "./threadManagement";

/**
 * Prepares the request data for backend API
 * @param messages - Array of thread messages
 * @param conversationId - Conversation ID if any
 * @param parent_id - Parent message ID if any
 * @returns Request data object
 */
export function prepareRequestData(
  messages: readonly ThreadMessage[],
  conversationId: number | null,
  parent_id: string | null | number,
) {
  const lastUserText = getLastUserMessage(messages);

  if (typeof parent_id === "number") {
    parent_id = parent_id.toString();
  }

  const requestData: {
    message: string;
    ai_conversation_id?: number;
    parent_id?: string | null;
  } = {
    message: lastUserText,
    parent_id: parent_id,
  };

  if (conversationId) {
    requestData.ai_conversation_id = conversationId;
  }

  return requestData;
}

/**
 * Sends request to backend API and returns a stream generator
 * @param requestData - Data to send in the request
 * @param abortSignal - Signal to abort the request
 * @param conversationId - Current conversation ID if any
 * @returns Stream generator for the response
 */
export async function sendBackendRequest(
  requestData: any,
  client: string,
  abortSignal?: AbortSignal,
  conversationId: number | null = null,
) {
  const response = await postAuthorizedStreaming(
    `${import.meta.env.VITE_BACKEND_URL}/${client}/stream-chat`,
    requestData,
    abortSignal,
  );

  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("Response body is unavailable");
  }

  return createStreamGenerator(
    reader as ReadableStreamDefaultReader<Uint8Array>,
    conversationId,
  );
}
